import { FloatingLayer } from "./FloatingLayer";
import React, { type CSSProperties, type ReactElement, type ReactNode } from "react";
import { arrow as arrowMiddleware, autoUpdate, flip, FloatingArrow, FloatingNode, FloatingTree, offset, safePolygon, shift, useClick, useDismiss, useFloating, useFloatingNodeId, useFloatingParentNodeId, useFloatingTree, useFocus, useHover, useInteractions, useListNavigation, useRole, useTransitionStyles, type Placement as FloatingPlacement } from "@floating-ui/react";
import { ExclamationCircleFilled, RightOutlined } from "@ant-design/icons";
import { Button, type ButtonProps } from "./Button";
import { cx } from "./fieldContext";
import { useUiText } from "./uiText";
import { KEY_ENTER, KEY_SPACE } from "./keyboardKeys";
const MS_PER_SECOND = 1000;
const TRANSITION_OPEN_MS = 120;
const TRANSITION_CLOSE_MS = 90;

/*
 * Überlagerungen des Kits (Plan 2026-10-13-antd-ausbau, Stufe 5): Tooltip, Popover, Popconfirm und Dropdown
 * auf `@floating-ui/react` statt Antd. Schnittstelle wie Antd im genutzten Umfang; Positionierung mit
 * Umklappen und Verschieben am Fensterrand, Escape und Klick daneben schließen.
 */

export type OverlayPlacement =
  | "top" | "topLeft" | "topRight"
  | "bottom" | "bottomLeft" | "bottomRight"
  | "left" | "leftTop" | "leftBottom"
  | "right" | "rightTop" | "rightBottom";

const PLACEMENT: Record<OverlayPlacement, FloatingPlacement> = {
  top: "top", topLeft: "top-start", topRight: "top-end",
  bottom: "bottom", bottomLeft: "bottom-start", bottomRight: "bottom-end",
  left: "left", leftTop: "left-start", leftBottom: "left-end",
  right: "right", rightTop: "right-start", rightBottom: "right-end",
};

/** Abstand zum Auslöser, zusätzlicher Abstand mit Pfeil und Mindestabstand zum Fensterrand (px, wie Antd). */
const POPUP_GAP = 4;
const ARROW_GAP = 4;
const EDGE = 8;

type TriggerType = "hover" | "focus" | "click" | "contextMenu";
type TriggerProp = TriggerType | TriggerType[];

const triggers = (value: TriggerProp | undefined, fallback: TriggerType[]): Set<TriggerType> =>
  new Set(value === undefined ? fallback : Array.isArray(value) ? value : [value]);

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value);
  else if (ref && typeof ref === "object") (ref as React.MutableRefObject<T | null>).current = value;
}

/**
 * Auslöser einer Überlagerung. Ein einzelnes Element bekommt Referenz und Ereignisse direkt (wie Antd);
 * gibt die Komponente keinen DOM-Knoten heraus, ist sie deaktiviert oder ist der Inhalt Text, umschließt
 * ein `span` den Inhalt. Deaktivierte Schaltflächen lösen selbst keine Mausereignisse aus — die Hülle schon.
 */
function OverlayAnchor({ children, setReference, setPositionReference, referenceProps }: {
  children: ReactNode;
  setReference: (node: Element | null) => void;
  setPositionReference: (node: Element | null) => void;
  referenceProps: (user?: Record<string, unknown>) => Record<string, unknown>;
}) {
  const child = React.isValidElement(children) && children.type !== React.Fragment ? (children as ReactElement<Record<string, unknown>>) : null;
  const disabled = child?.props.disabled === true;
  const [wrap, setWrap] = React.useState(false);
  const useWrapper = wrap || !child || disabled;
  const elementRef = React.useRef<Element | null>(null);
  const childRef = child?.props.ref as React.Ref<unknown> | undefined;

  const cloneRef = React.useCallback((node: unknown) => {
    elementRef.current = node instanceof Element ? node : null;
    setReference(elementRef.current);
    assignRef(childRef, node);
  }, [childRef, setReference]);

  const wrapperRef = React.useCallback((node: HTMLSpanElement | null) => {
    setReference(node);
    // `display: contents` hat keine eigene Box; positioniert wird am umschlossenen Element.
    if (node && child && !disabled) setPositionReference(node.firstElementChild);
  }, [child, disabled, setPositionReference, setReference]);

  React.useLayoutEffect(() => {
    if (!useWrapper && !elementRef.current) setWrap(true);
  }, [useWrapper]);

  if (useWrapper) {
    const block = child?.props.block === true;
    return (
      <span
        ref={wrapperRef}
        className={cx("ck-overlay-anchor", child && !disabled && "ck-overlay-anchor--contents", disabled && "is-disabled", block && "is-block")}
        {...referenceProps()}
      >
        {children}
      </span>
    );
  }
  return React.cloneElement(child!, referenceProps({ ...child!.props, ref: cloneRef }));
}

type PopupBaseOptions = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: OverlayPlacement;
  trigger: Set<TriggerType>;
  role: "tooltip" | "dialog" | "menu";
  arrow: boolean;
  disabled?: boolean;
  gap: number;
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  nodeId?: string;
};

function usePopup({ open: openProp, defaultOpen, onOpenChange, placement = "top", trigger, role, arrow, disabled, gap, mouseEnterDelay = 0.1, mouseLeaveDelay = 0.1, nodeId }: PopupBaseOptions) {
  const [innerOpen, setInnerOpen] = React.useState(Boolean(defaultOpen));
  const open = !disabled && (openProp ?? innerOpen);
  const setOpen = React.useCallback((next: boolean) => {
    if (openProp === undefined) setInnerOpen(next);
    onOpenChange?.(next);
  }, [onOpenChange, openProp]);
  const arrowRef = React.useRef<SVGSVGElement>(null);
  const floating = useFloating({
    nodeId,
    open,
    onOpenChange: (next) => { if (!disabled) setOpen(next); },
    placement: PLACEMENT[placement] ?? "top",
    whileElementsMounted: autoUpdate,
    middleware: [offset(arrow ? gap + ARROW_GAP : gap), flip({ padding: EDGE }), shift({ padding: EDGE, crossAxis: nodeId !== undefined }), ...(arrow ? [arrowMiddleware({ element: arrowRef, padding: EDGE })] : [])],
  });
  const hover = useHover(floating.context, {
    enabled: trigger.has("hover") && !disabled,
    delay: { open: mouseEnterDelay * MS_PER_SECOND, close: mouseLeaveDelay * MS_PER_SECOND },
    handleClose: role === "tooltip" ? null : safePolygon(),
  });
  const focus = useFocus(floating.context, { enabled: (trigger.has("focus") || (role === "tooltip" && trigger.has("hover"))) && !disabled });
  const click = useClick(floating.context, { enabled: trigger.has("click") && !disabled });
  const dismiss = useDismiss(floating.context, { bubbles: { escapeKey: role !== "menu" } });
  const roleProps = useRole(floating.context, { role });
  const transition = useTransitionStyles(floating.context, { duration: { open: TRANSITION_OPEN_MS, close: TRANSITION_CLOSE_MS }, initial: { opacity: 0, transform: "scale(0.96)" } });
  return { open, setOpen, floating, arrowRef, interactions: [hover, focus, click, dismiss, roleProps], transition };
}

function portalRoot(getPopupContainer: ((node: HTMLElement) => HTMLElement) | undefined, reference: Element | null) {
  return getPopupContainer && reference instanceof HTMLElement ? getPopupContainer(reference) : undefined;
}

// ---------------------------------------------------------------- Tooltip

export interface TooltipProps {
  title?: ReactNode | (() => ReactNode);
  /** Antd-Kompatibilität: Popover-ähnliche Inhalte über `overlay`. */
  overlay?: ReactNode;
  placement?: OverlayPlacement;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: TriggerProp;
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  arrow?: boolean;
  /** Antd-Farbe der Blase; im Kit ohne Wirkung, die Blase folgt dem Theme. */
  color?: string;
  getPopupContainer?: (node: HTMLElement) => HTMLElement;
  /** Antd-Ausrichtungsoptionen; das Kit klappt und verschiebt immer selbst. */
  align?: unknown;
  overlayClassName?: string;
  rootClassName?: string;
  overlayStyle?: CSSProperties;
  zIndex?: number;
  destroyOnHidden?: boolean;
  children?: ReactNode;
}

/** Hinweisblase (Ersatz für Antd `Tooltip`): bei Zeigen und Fokus, Escape schließt. */
export function Tooltip({ title, overlay, placement = "top", open, defaultOpen, onOpenChange, trigger, mouseEnterDelay, mouseLeaveDelay, arrow = true, getPopupContainer, overlayClassName, rootClassName, overlayStyle, zIndex, children }: TooltipProps) {
  const content = typeof title === "function" ? title() : title ?? overlay;
  const empty = content == null || content === false || content === "";
  const popup = usePopup({ open, defaultOpen, onOpenChange, placement, trigger: triggers(trigger, ["hover"]), role: "tooltip", arrow, disabled: empty, gap: POPUP_GAP, mouseEnterDelay, mouseLeaveDelay });
  const { getReferenceProps, getFloatingProps } = useInteractions(popup.interactions);
  const { refs, floatingStyles, context } = popup.floating;
  return (
    <>
      <OverlayAnchor setReference={refs.setReference} setPositionReference={refs.setPositionReference} referenceProps={getReferenceProps}>{children}</OverlayAnchor>
      {popup.transition.isMounted && !empty ? (
        <FloatingLayer root={portalRoot(getPopupContainer, popup.floating.elements.domReference)}>
          <div
            ref={refs.setFloating}
            className={cx("ck-tooltip", overlayClassName, rootClassName)}
            style={{ ...floatingStyles, zIndex: zIndex ?? 1070 }}
            {...getFloatingProps()}
          >
            <div className="ck-tooltip-inner" style={{ ...popup.transition.styles, ...overlayStyle }}>
              {content}
              {arrow ? <FloatingArrow ref={popup.arrowRef} context={context} className="ck-tooltip-arrow" width={12} height={6} /> : null}
            </div>
          </div>
        </FloatingLayer>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- Popover

export interface PopoverProps {
  content?: ReactNode | (() => ReactNode);
  title?: ReactNode | (() => ReactNode);
  placement?: OverlayPlacement;
  trigger?: TriggerProp;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  arrow?: boolean;
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  getPopupContainer?: (node: HTMLElement) => HTMLElement;
  overlayClassName?: string;
  rootClassName?: string;
  overlayStyle?: CSSProperties;
  overlayInnerStyle?: CSSProperties;
  classNames?: { root?: string; body?: string };
  styles?: { root?: CSSProperties; body?: CSSProperties; container?: CSSProperties };
  zIndex?: number;
  destroyOnHidden?: boolean;
  children?: ReactNode;
}

/** Aufklappfläche mit Titel und Inhalt (Ersatz für Antd `Popover`). */
export function Popover({ content, title, placement = "top", trigger, open, defaultOpen, onOpenChange, arrow = true, mouseEnterDelay, mouseLeaveDelay, getPopupContainer, overlayClassName, rootClassName, overlayStyle, overlayInnerStyle, classNames, styles, zIndex, children }: PopoverProps) {
  const body = typeof content === "function" ? content() : content;
  const head = typeof title === "function" ? title() : title;
  const empty = (body == null || body === false) && (head == null || head === false);
  const set = triggers(trigger, ["hover"]);
  const popup = usePopup({ open, defaultOpen, onOpenChange, placement, trigger: set, role: set.has("click") ? "dialog" : "tooltip", arrow, disabled: empty, gap: POPUP_GAP, mouseEnterDelay, mouseLeaveDelay });
  const { getReferenceProps, getFloatingProps } = useInteractions(popup.interactions);
  const { refs, floatingStyles, context } = popup.floating;
  return (
    <>
      <OverlayAnchor setReference={refs.setReference} setPositionReference={refs.setPositionReference} referenceProps={getReferenceProps}>{children}</OverlayAnchor>
      {popup.transition.isMounted && !empty ? (
        <FloatingLayer root={portalRoot(getPopupContainer, popup.floating.elements.domReference)}>
          <div
            ref={refs.setFloating}
            className={cx("ck-popover", overlayClassName, rootClassName, classNames?.root)}
            style={{ ...floatingStyles, zIndex: zIndex ?? 1030, ...overlayStyle, ...styles?.root }}
            {...getFloatingProps()}
          >
            <div className={cx("ck-popover-inner", classNames?.body)} style={{ ...popup.transition.styles, ...overlayInnerStyle, ...styles?.container, ...styles?.body }}>
              {head != null && head !== false ? <div className="ck-popover-title">{head}</div> : null}
              {body != null && body !== false ? <div className="ck-popover-content">{body}</div> : null}
              {arrow ? <FloatingArrow ref={popup.arrowRef} context={context} className="ck-popover-arrow" width={14} height={7} /> : null}
            </div>
          </div>
        </FloatingLayer>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- Popconfirm

export interface PopconfirmProps {
  title?: ReactNode;
  description?: ReactNode;
  onConfirm?: (event?: React.MouseEvent<HTMLElement>) => unknown;
  onCancel?: (event?: React.MouseEvent<HTMLElement>) => void;
  okText?: ReactNode;
  cancelText?: ReactNode;
  okType?: "primary" | "danger" | "default";
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  showCancel?: boolean;
  icon?: ReactNode;
  placement?: OverlayPlacement;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  getPopupContainer?: (node: HTMLElement) => HTMLElement;
  overlayClassName?: string;
  rootClassName?: string;
  zIndex?: number;
  children?: ReactNode;
}

/** Kurze Rückfrage direkt am Auslöser (Ersatz für Antd `Popconfirm`); ein Versprechen in `onConfirm` hält den Ladezustand. */
export function Popconfirm({ title, description, onConfirm, onCancel, okText, cancelText, okType = "primary", okButtonProps, cancelButtonProps, showCancel = true, icon, placement = "top", disabled, open, onOpenChange, getPopupContainer, overlayClassName, rootClassName, zIndex, children }: PopconfirmProps) {
  const { text } = useUiText();
  const [busy, setBusy] = React.useState(false);
  const popup = usePopup({ open, onOpenChange, placement, trigger: new Set(["click"]), role: "dialog", arrow: true, disabled, gap: POPUP_GAP });
  const { getReferenceProps, getFloatingProps } = useInteractions(popup.interactions);
  const { refs, floatingStyles, context } = popup.floating;
  const confirm = async (event: React.MouseEvent<HTMLElement>) => {
    const result = onConfirm?.(event);
    if (result && typeof (result as Promise<unknown>).then === "function") {
      setBusy(true);
      try { await result; popup.setOpen(false); } catch { /* Fehler meldet der Aufrufer; die Rückfrage bleibt offen. */ } finally { setBusy(false); }
      return;
    }
    popup.setOpen(false);
  };
  return (
    <>
      <OverlayAnchor setReference={refs.setReference} setPositionReference={refs.setPositionReference} referenceProps={getReferenceProps}>{children}</OverlayAnchor>
      {popup.transition.isMounted ? (
        <FloatingLayer root={portalRoot(getPopupContainer, popup.floating.elements.domReference)}>
          <div ref={refs.setFloating} className={cx("ck-popover ck-popconfirm", overlayClassName, rootClassName)} style={{ ...floatingStyles, zIndex: zIndex ?? 1060 }} {...getFloatingProps()}>
            <div className="ck-popover-inner" style={popup.transition.styles}>
              <div className="ck-popconfirm-message">
                <span className="ck-popconfirm-message-icon">{icon ?? <ExclamationCircleFilled />}</span>
                <div className="ck-popconfirm-message-text">
                  {title != null ? <div className="ck-popconfirm-title">{title}</div> : null}
                  {description != null ? <div className="ck-popconfirm-description">{description}</div> : null}
                </div>
              </div>
              <div className="ck-popconfirm-buttons">
                {showCancel ? (
                  <Button size="small" {...cancelButtonProps} onClick={(event) => { onCancel?.(event as React.MouseEvent<HTMLElement>); popup.setOpen(false); }}>
                    {cancelText ?? text("Cancel")}
                  </Button>
                ) : null}
                <Button
                  size="small"
                  type={okType === "default" ? "default" : "primary"}
                  danger={okType === "danger"}
                  loading={busy}
                  {...okButtonProps}
                  onClick={(event) => confirm(event as React.MouseEvent<HTMLElement>)}
                >
                  {okText ?? text("OK")}
                </Button>
              </div>
              <FloatingArrow ref={popup.arrowRef} context={context} className="ck-popover-arrow" width={14} height={7} />
            </div>
          </div>
        </FloatingLayer>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- Dropdown (Menü)

export type MenuInfo = {
  key: string;
  keyPath: string[];
  domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
};

export type MenuItemObject = {
  key?: React.Key;
  label?: ReactNode;
  icon?: ReactNode;
  extra?: ReactNode;
  title?: string;
  danger?: boolean;
  disabled?: boolean;
  type?: "item" | "divider" | "group" | "submenu";
  children?: MenuItem[];
  onClick?: (info: MenuInfo) => void;
  className?: string;
  style?: CSSProperties;
  dashed?: boolean;
};
export type MenuItem = MenuItemObject | null | false | undefined;

export interface MenuProps {
  items?: MenuItem[];
  onClick?: (info: MenuInfo) => void;
  selectable?: boolean;
  selectedKeys?: React.Key[];
  className?: string;
  style?: CSSProperties;
}

export interface DropdownProps {
  menu?: MenuProps;
  trigger?: ("click" | "hover" | "contextMenu")[];
  placement?: OverlayPlacement;
  open?: boolean;
  onOpenChange?: (open: boolean, info: { source: "trigger" | "menu" }) => void;
  disabled?: boolean;
  /** Eigener Inhalt statt oder um das Menü (Antd `popupRender`, früher `dropdownRender`). */
  popupRender?: (menu: ReactNode) => ReactNode;
  dropdownRender?: (menu: ReactNode) => ReactNode;
  arrow?: boolean;
  getPopupContainer?: (node: HTMLElement) => HTMLElement;
  overlayClassName?: string;
  rootClassName?: string;
  overlayStyle?: CSSProperties;
  zIndex?: number;
  destroyOnHidden?: boolean;
  autoFocus?: boolean;
  children?: ReactNode;
}

type FlatEntry =
  | { kind: "item"; item: MenuItemObject; key: string; index: number; depth: number; keyPath: string[] }
  | { kind: "submenu"; item: MenuItemObject; key: string; index: number; depth: number; keyPath: string[] }
  | { kind: "divider"; key: string; dashed?: boolean }
  | { kind: "group"; key: string; label: ReactNode };

/** Nur Gruppen werden eingeebnet. Untermenüs besitzen eine eigene positionierte Ebene. */
function flatten(items: MenuItem[] | undefined, depth = 0, parents: string[] = [], out: FlatEntry[] = [], counter = { index: 0 }): FlatEntry[] {
  (items ?? []).forEach((item, position) => {
    if (!item) return;
    const key = String(item.key ?? `${depth}-${position}`);
    if (item.type === "divider") { out.push({ kind: "divider", key, dashed: item.dashed }); return; }
    if (item.type === "group") {
      if (item.label != null) out.push({ kind: "group", key, label: item.label });
      flatten(item.children, depth, parents, out, counter);
      return;
    }
    const keyPath = [key, ...parents];
    if (item.children?.length) {
      out.push({ kind: "submenu", item, key, index: counter.index++, depth, keyPath });
      return;
    }
    out.push({ kind: "item", item, key, index: counter.index++, depth, keyPath });
  });
  return out;
}

/** Aufklappmenü (Ersatz für Antd `Dropdown`); Pfeiltasten wählen, Enter löst aus, Escape schließt. */
export function Dropdown(props: DropdownProps) {
  return <FloatingTree><DropdownInner {...props} /></FloatingTree>;
}

function DropdownInner({ menu, trigger = ["hover"], placement = "bottomLeft", open, onOpenChange, disabled, popupRender, dropdownRender, arrow = false, getPopupContainer, overlayClassName, rootClassName, overlayStyle, zIndex, children, parents = [] }: DropdownProps & { parents?: string[] }) {
  const nodeId = useFloatingNodeId();
  const parentId = useFloatingParentNodeId();
  const tree = useFloatingTree();
  const set = new Set<TriggerType>(trigger);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const listRef = React.useRef<Array<HTMLElement | null>>([]);
  const popup = usePopup({
    open,
    onOpenChange: (next) => {
      if (next) tree?.events.emit("submenuopen", { nodeId, parentId });
      onOpenChange?.(next, { source: "trigger" });
    },
    placement, trigger: set, role: "menu", arrow, disabled, gap: POPUP_GAP, nodeId,
  });
  const { setOpen: setPopupOpen } = popup;
  const { refs, floatingStyles, context } = popup.floating;
  const entries = flatten(menu?.items, 0, parents);
  const navigation = useListNavigation(context, { listRef, activeIndex, onNavigate: setActiveIndex, loop: true, nested: parentId !== null,
    disabledIndices: entries.flatMap(entry => "item" in entry && entry.item.disabled ? [entry.index] : []) });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([...popup.interactions, navigation]);
  const selected = new Set((menu?.selectedKeys ?? []).map(String));

  React.useEffect(() => {
    const close = () => {
      if (open === undefined) setPopupOpen(false);
      else onOpenChange?.(false, { source: "menu" });
    };
    const siblingOpened = (event: { nodeId: string; parentId: string | null }) => {
      if (event.nodeId !== nodeId && event.parentId === parentId) setPopupOpen(false);
    };
    tree?.events.on("select", close);
    tree?.events.on("submenuopen", siblingOpened);
    return () => { tree?.events.off("select", close); tree?.events.off("submenuopen", siblingOpened); };
  }, [tree, nodeId, parentId, setPopupOpen, open, onOpenChange]);

  const choose = (entry: Extract<FlatEntry, { kind: "item" }>, domEvent: MenuInfo["domEvent"]) => {
    if (entry.item.disabled) return;
    const info: MenuInfo = { key: entry.key, keyPath: entry.keyPath, domEvent };
    entry.item.onClick?.(info);
    menu?.onClick?.(info);
    tree?.events.emit("select");
  };

  const menuNode = (
    <ul className={cx("ck-dropdown-menu", menu?.className)} style={menu?.style} role="menu">
      {entries.map((entry) => {
        if (entry.kind === "divider") return <li key={entry.key} role="separator" className={cx("ck-dropdown-menu-item-divider", entry.dashed && "is-dashed")} />;
        if (entry.kind === "group") return <li key={entry.key} role="presentation" className="ck-dropdown-menu-item-group-title">{entry.label}</li>;
        const { item } = entry;
        const submenu = entry.kind === "submenu";
        const row = (
          <li
            key={entry.key}
            ref={(node) => { listRef.current[entry.index] = node; }}
            role="menuitem"
            tabIndex={activeIndex === entry.index ? 0 : -1}
            aria-disabled={item.disabled || undefined}
            aria-haspopup={submenu ? "menu" : undefined}
            title={item.title}
            className={cx(
              "ck-dropdown-menu-item",
              submenu && "ck-dropdown-menu-submenu-title",
              item.danger && "ck-dropdown-menu-item-danger",
              item.disabled && "ck-dropdown-menu-item-disabled",
              activeIndex === entry.index && "ck-dropdown-menu-item-active",
              selected.has(entry.key) && "ck-dropdown-menu-item-selected",
              item.className,
            )}
            style={item.style}
            {...getItemProps({
              onClick: (event: React.MouseEvent<HTMLElement>) => {
                if (entry.kind === "submenu") return;
                choose(entry, event);
              },
              onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
                if (entry.kind === "submenu") return;
                if (event.key === KEY_ENTER || event.key === KEY_SPACE) { event.preventDefault(); choose(entry, event); }
              },
            })}
          >
            {item.icon ? <span className="ck-dropdown-menu-item-icon">{item.icon}</span> : null}
            <span className="ck-dropdown-menu-title-content">{item.label}</span>
            {item.extra != null ? <span className="ck-dropdown-menu-item-extra">{item.extra}</span> : null}
            {submenu ? <RightOutlined className="ck-dropdown-menu-submenu-arrow" aria-hidden /> : null}
          </li>
        );
        return submenu ? <DropdownInner key={entry.key} parents={entry.keyPath} trigger={["hover", "click"]} placement="rightTop" disabled={item.disabled}
          menu={{ items: item.children, selectedKeys: menu?.selectedKeys, onClick: menu?.onClick }}
          getPopupContainer={getPopupContainer} zIndex={zIndex}>{row}</DropdownInner> : row;
      })}
    </ul>
  );
  const render = popupRender ?? dropdownRender;
  return (
    <FloatingNode id={nodeId}>
      <OverlayAnchor setReference={refs.setReference} setPositionReference={refs.setPositionReference}
        referenceProps={props => {
          const merged = getReferenceProps(props);
          return {
            ...merged,
            ...(parentId !== null ? { role: "menuitem" } : {}),
            ...(set.has("contextMenu") ? {
              onContextMenu: (event: React.MouseEvent<HTMLElement>) => {
                (merged.onContextMenu as React.MouseEventHandler<HTMLElement> | undefined)?.(event);
                if (disabled || event.defaultPrevented) return;
                event.preventDefault();
                const { clientX: x, clientY: y, currentTarget } = event;
                refs.setPositionReference({
                  contextElement: currentTarget,
                  getBoundingClientRect: () => ({ x, y, left: x, right: x, top: y, bottom: y, width: 0, height: 0 }),
                });
                setPopupOpen(true);
              },
            } : {}),
          };
        }}>{children}</OverlayAnchor>
      {popup.transition.isMounted ? (
        <FloatingLayer root={portalRoot(getPopupContainer, popup.floating.elements.domReference)}>
            <div
              ref={refs.setFloating}
              className={cx("ck-dropdown", overlayClassName, rootClassName)}
              style={{ ...floatingStyles, zIndex: zIndex ?? 1050, ...overlayStyle }}
              {...getFloatingProps()}
            >
              <div style={popup.transition.styles}>{render ? render(menuNode) : menuNode}</div>
              {arrow ? <FloatingArrow ref={popup.arrowRef} context={context} className="ck-dropdown-arrow" width={14} height={7} /> : null}
            </div>
        </FloatingLayer>
      ) : null}
    </FloatingNode>
  );
}
